import {
  Autocomplete,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
} from '@dls/react-core';
import React, { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { StyledBlueHeading } from '../styled/styles';
import { FormHelperText, Grid, Typography } from '@material-ui/core';
import { GridFormElement } from '../form-controls';
import { updateUserRoles, User } from '../../api/studykit';
import { FormFooter } from '../styled/form-footer';
import { NotiStackAction } from '../styled/notistack-action';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  studyId: string;
  user?: User;
  onRolesUpdated: () => void;
}

export const groupToRole: Record<string, string> = {
  Supervisor: 'SUPERVISOR',
  ComplianceChecker: 'COMPLIANCECHECKER',
  DataProvider: 'DATAPROVIDER',
  AutomatedDataProvider: 'AUTOMATEDDATAPROVIDER',
  DataConsumer: 'DATACONSUMER',
  Deleter: 'DELETER',
};

const roleToGroup: Record<string, string> = {
  SUPERVISOR: 'Supervisor',
  COMPLIANCECHECKER: 'ComplianceChecker',
  DATAPROVIDER: 'DataProvider',
  AUTOMATEDDATAPROVIDER: 'AutomatedDataProvider',
  DATACONSUMER: 'DataConsumer',
  DELETER: 'Deleter',
};

export const EditStudyUserDialog: React.FC<DialogProps> = ({
  open,
  onClose,
  studyId,
  user,
  onRolesUpdated,
}) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setSelectedRoles(user?.groups.map((g) => groupToRole[g]) || []);
  }, [user]);

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();
    if (!user) return;

    setIsSubmitting(true);
    updateUserRoles(studyId, user.email, selectedRoles)
      .then(() => {
        enqueueSnackbar('Roles have successfully been updated!', {
          variant: 'success',
          action: <NotiStackAction close={closeSnackbar} />,
        });
        onRolesUpdated();
        onClose();
      })
      .catch(() => {
        enqueueSnackbar('Could not update the roles', {
          variant: 'error',
          action: <NotiStackAction close={closeSnackbar} />,
        });
      })
      .finally(() => setIsSubmitting(false));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { width: `50vw`, maxWidth: `600px` } }}
    >
      <DialogTitle disableTypography>
        <StyledBlueHeading variant="h2">Edit user</StyledBlueHeading>
        <Typography variant="caption" style={{ marginTop: -8 }}>
          {user?.email || ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <GridFormElement label={'Roles'} md={12}>
              <Autocomplete
                multiple
                fullWidth
                PaperComponent={Paper}
                classes={{
                  paper: 'DLS-Autocomplete',
                }}
                renderTags={(value: string[], getTagProps: any) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      variant="default"
                      label={roleToGroup[option]}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                getOptionLabel={(m: string) => roleToGroup[m]}
                options={Object.keys(roleToGroup)}
                onChange={(_: any, v: string[]) => setSelectedRoles([...v])}
                disableClearable
                renderInput={(params: any) => <TextField {...params} variant="standard" />}
                value={selectedRoles}
              />
              {selectedRoles.length === 0 && open && (
                <FormHelperText error>Select at least one role</FormHelperText>
              )}
            </GridFormElement>
          </Grid>
          <FormFooter
            onCancel={onClose}
            isSubmitting={isSubmitting}
            isSubmitDisabled={selectedRoles.length === 0}
          />
        </form>
      </DialogContent>
    </Dialog>
  );
};
